import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import {
    getPerimeterFilterErrorKey,
    isPerimeterColumnShown,
    isPerimeterFilterShown,
} from "components/perimeterFilter/perimeterListFilter";

// Shared empty value so the lists' effects do not rerun on every render.
const NO_PERIMETER_IDS = [];

// Perimeter filter of a list page. Lives in the page around the tabs so the
// options are read once per opened list and the choice survives a tab change.
// A failed read leaves options null: no button, no column, lists as before.
const usePerimeterListFilter = () => {
    const { language } = UseContext();
    // The context builds a new language manager on each render: read it through
    // a ref so the refusal handler stays stable for the lists' effects.
    const languageRef = useRef(language);
    languageRef.current = language;
    const [options, setOptions] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        let cancelled = false;
        ApiDatabase.getPerimeterOptions(
            {},
            (res) => {
                if (cancelled) return;
                setOptions(res?.data || null);
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
                setOptions(null);
            }
        );
        return () => {
            cancelled = true;
        };
    }, []);

    const isFilterShown = isPerimeterFilterShown(options);

    // Returns true when the error was a refusal of the filter, then cleared.
    const handlePerimeterFilterRefusal = useCallback(
        (err) => {
            const messageKey = getPerimeterFilterErrorKey(err);
            if (!messageKey) return false;
            toast.error(languageRef.current.getValue(messageKey));
            setSelectedIds([]);
            return true;
        },
        []
    );

    return {
        options,
        isFilterShown,
        isColumnShown: isPerimeterColumnShown(options),
        selectedIds,
        setSelectedIds,
        // Never send a choice the button cannot show or clear.
        perimeterIds: isFilterShown ? selectedIds : NO_PERIMETER_IDS,
        handlePerimeterFilterRefusal,
    };
};

export default usePerimeterListFilter;
